import { NavLink } from './NavLink';
import {
  SignedIn,
  SignedOut,
  SignInButton,
  SignOutButton,
  useUser,
} from '@clerk/clerk-react';
import { Briefcase, LogOut } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '@/shared/contexts/LanguageContext';
import { cn } from '@/shared/lib/utils';
import { InteraLogo } from '@/shared/components/logo/InteraLogo';
import { UserSettings } from '@/shared/components/user-button/UserSettings';
import { Button } from '@/shared/components/ui/Button';

export interface DesktopMenuProps {
  isPublic: boolean;
}

export const DesktopMenu = ({ isPublic }: DesktopMenuProps) => {
  const { t } = useTranslation('common');
  const { isRTL } = useLanguage();
  const { user } = useUser();

  const userName =
    user?.fullName ?? user?.firstName ?? user?.username ?? undefined;

  const userButtonAppearance = {
    elements: {
      avatarImage: {
        width: '50px',
        height: '50px',
      },
      avatarBox: {
        width: '50px',
        height: '50px',
      },
    },
  } as const;

  if (isPublic) {
    return (
      <div className="flex flex-col gap-6 p-6 text-white rounded-2xl">
        {/* Logo & User */}
        <div className="flex flex-col items-center gap-4 text-center">
          <InteraLogo />

          <SignedIn>
            <div className="flex flex-col items-center gap-2">
              <UserSettings appearance={userButtonAppearance} />
              {userName && (
                <span className="text-base leading-tight">{userName}</span>
              )}
            </div>
          </SignedIn>

          <SignedOut>
            <SignInButton />
          </SignedOut>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6 p-6 text-white rounded-2xl">
      {/* Logo & User */}
      <div className="flex flex-col items-center gap-4 text-center">
        <InteraLogo />

        <SignedIn>
          <div className="flex flex-col items-center gap-2">
            <UserSettings appearance={userButtonAppearance} />
            {userName && (
              <span className="text-base leading-tight">{userName}</span>
            )}
          </div>
        </SignedIn>

        <SignedOut>
          <SignInButton />
        </SignedOut>
      </div>

      <div className="w-full h-px bg-white/20" />

      {/* Navigation Links */}
      <nav
        className={cn(
          'flex flex-col gap-2',
          isRTL ? 'items-end' : 'items-start'
        )}
      >
        <NavLink
          to="/deal-intelligence"
          label={t('dealIntelligence')}
          icon={Briefcase}
        />
      </nav>

      <div className="w-full h-px bg-white/20" />

      {/* Sign Out */}
      <SignedIn>
        <SignOutButton>
          <Button
            variant="ghost"
            className={cn(
              'relative w-full justify-start gap-2 rounded-lg px-3 py-2 text-sm font-semibold text-white/80 hover:bg-white/10 hover:text-white focus-visible:ring-white focus-visible:ring-offset-0'
            )}
          >
            <LogOut aria-hidden className="w-4 h-4 shrink-0" />
            <span className="truncate">{t('signOut')}</span>
          </Button>
        </SignOutButton>
      </SignedIn>
    </div>
  );
};
